import type { StationSnapshot } from '@/domain';
import type { StationId } from '@/domain/types';
import type { StationRepository } from '@/persistence/ports';
import type { Evse3Database, StationRow, TopologyRow } from '../db';
import { computeDisplayChecksum } from '@/lib/computeDisplayChecksum';

/**
 * Persists station identity + topology atomically. Deleting a station cascades
 * across every per-station table to satisfy the "no orphans" invariant
 * (CLAUDE.md §4.7).
 */
export class StationRepositoryDexie implements StationRepository {
  constructor(private readonly db: Evse3Database) {}

  async list(): Promise<readonly StationSnapshot[]> {
    const stations = await this.db.stations.toArray();
    return Promise.all(stations.map((row) => this.hydrate(row)));
  }

  async get(id: StationId): Promise<StationSnapshot | null> {
    const row = await this.db.stations.get(id);
    if (!row) return null;
    return this.hydrate(row);
  }

  async findByIdentity(cpid: string, wsUrl: string): Promise<StationSnapshot | null> {
    const row = await this.db.stations.where('[cpid+wsUrl]').equals([cpid, wsUrl]).first();
    if (!row) return null;
    return this.hydrate(row);
  }

  async upsert(snap: StationSnapshot): Promise<void> {
    const row: StationRow = {
      id: snap.id,
      cpid: snap.cpid,
      wsUrl: snap.wsUrl,
      ...(snap.displayName !== undefined ? { displayName: snap.displayName } : {}),
      displayChecksum: computeDisplayChecksum(snap.wsUrl),
      ocppVersion: snap.ocppVersion,
      createdAt: snap.createdAt,
      ...(snap.lastConnectedAt !== undefined ? { lastConnectedAt: snap.lastConnectedAt } : {}),
    };
    const topo: TopologyRow = { stationId: snap.id, ...snap.topology };
    await this.db.transaction('rw', this.db.stations, this.db.topologies, async () => {
      await this.db.stations.put(row);
      await this.db.topologies.put(topo);
    });
  }

  async delete(id: StationId): Promise<void> {
    await this.db.transaction(
      'rw',
      [
        this.db.stations,
        this.db.topologies,
        this.db.configurations,
        this.db.chargingProfiles,
        this.db.transactions,
        this.db.meters,
        this.db.offlineQueue,
        this.db.eventLog,
        this.db.snapshots,
        this.db.localAuthList,
        this.db.localAuthListVersions,
        // FEAT-011 — connectorState is per-station and must cascade.
        // vehicleProfiles is workspace-wide and is NOT cascaded.
        this.db.connectorState,
        // TG-B — per-transaction progress series.
        this.db.transactionSamples,
      ],
      async () => {
        await this.db.stations.delete(id);
        await this.db.topologies.delete(id);
        await this.db.configurations.where('stationId').equals(id).delete();
        await this.db.chargingProfiles.where('stationId').equals(id).delete();
        await this.db.transactions.where('stationId').equals(id).delete();
        await this.db.meters.where({ stationId: id }).delete();
        await this.db.offlineQueue.where('stationId').equals(id).delete();
        await this.db.eventLog.where('stationId').equals(id).delete();
        await this.db.snapshots.where('stationId').equals(id).delete();
        await this.db.localAuthList.where('stationId').equals(id).delete();
        await this.db.localAuthListVersions.delete(id);
        await this.db.connectorState.where('stationId').equals(id).delete();
        await this.db.transactionSamples.where('stationId').equals(id).delete();
      },
    );
  }

  private async hydrate(row: StationRow): Promise<StationSnapshot> {
    const topo = await this.db.topologies.get(row.id);
    const topology = topo
      ? { cpMaxPowerW: topo.cpMaxPowerW, evses: topo.evses }
      : { cpMaxPowerW: 0, evses: [] };
    return {
      id: row.id,
      cpid: row.cpid,
      wsUrl: row.wsUrl,
      ...(row.displayName !== undefined ? { displayName: row.displayName } : {}),
      ocppVersion: row.ocppVersion as StationSnapshot['ocppVersion'],
      topology,
      createdAt: row.createdAt,
      ...(row.lastConnectedAt !== undefined ? { lastConnectedAt: row.lastConnectedAt } : {}),
    };
  }
}
